import React from 'react';
import { Clock, MapPin, Baby, Ticket, Sun, Landmark, Star, Music, PartyPopper, Theater, Trophy, UtensilsCrossed, Church, CalendarDays } from 'lucide-react';

export const CATEGORY_ICONS = { concerts: Music, festivals: PartyPopper, cultural: Theater, sports: Trophy, gastronomy: UtensilsCrossed, traditional: Church, general: CalendarDays };
export const CATEGORY_COLORS = { concerts: 'bg-purple-500', festivals: 'bg-pink-500', cultural: 'bg-blue-500', sports: 'bg-green-500', gastronomy: 'bg-orange-500', traditional: 'bg-amber-600', general: 'bg-gray-500' };

const CATEGORY_IMAGES = {
  concerts: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=900&q=70&fm=webp',
  festivals: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=900&q=70&fm=webp',
  cultural: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=900&q=70&fm=webp',
  sports: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=900&q=70&fm=webp',
  gastronomy: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=70&fm=webp',
  traditional: 'https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?w=900&q=70&fm=webp',
  markets: 'https://images.unsplash.com/photo-1488459716781-31db52582fe9?w=900&q=70&fm=webp',
  general: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=900&q=70&fm=webp',
};

const T = {
  es: { today: 'Hoy', tomorrow: 'Mañana', allDay: 'Todo el día', until: 'hasta el', kids: 'Para niños', free: 'Gratis', outdoor: 'Al aire libre', traditional: 'Tradicional', featured: 'Destacado' },
  en: { today: 'Today', tomorrow: 'Tomorrow', allDay: 'All day', until: 'until', kids: 'For kids', free: 'Free', outdoor: 'Outdoors', traditional: 'Traditional', featured: 'Featured' },
  de: { today: 'Heute', tomorrow: 'Morgen', allDay: 'Ganztägig', until: 'bis', kids: 'Für Kinder', free: 'Gratis', outdoor: 'Im Freien', traditional: 'Traditionell', featured: 'Highlight' },
};
const LOCALE = { es: 'es-ES', en: 'en-GB', de: 'de-DE' };

export const canaryToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Atlantic/Canary', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtDay = (iso, lang, opts = { weekday: 'long', day: 'numeric', month: 'short' }) => new Date(`${iso}T12:00:00`).toLocaleDateString(LOCALE[lang] || 'es-ES', opts);

export const isFreeEvent = (e) => e.is_free === true || (e.is_free !== false && /gratis|gratuit|entrada libre|free|kostenlos|^0\s*€|^0€/i.test(e.price_info || ''));

const eventImage = (e) => e.image || CATEGORY_IMAGES[e.category] || CATEGORY_IMAGES.general;

const Tags = ({ e, t }) => {
  const tags = [
    e.kids_friendly && { k: 'kids', Icon: Baby, cls: 'bg-amber-100 text-amber-800', label: t.kids },
    isFreeEvent(e) && { k: 'free', Icon: Ticket, cls: 'bg-emerald-100 text-emerald-800', label: t.free },
    e.outdoor && { k: 'outdoor', Icon: Sun, cls: 'bg-sky-100 text-sky-800', label: t.outdoor },
    e.traditional && { k: 'traditional', Icon: Landmark, cls: 'bg-orange-100 text-[#9C4A2E]', label: t.traditional },
  ].filter(Boolean).slice(0, 3);
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap gap-1 justify-end" data-testid="event-tags">
      {tags.map(({ k, Icon, cls, label }) => (
        <span key={k} className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${cls}`} data-testid={`event-tag-${k}`}><Icon size={10} />{label}</span>
      ))}
    </div>
  );
};

const CategoryChip = ({ e, categories, language }) => {
  const Icon = CATEGORY_ICONS[e.category] || CalendarDays;
  const cat = categories.find(c => c.slug === e.category);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold text-white px-2 py-0.5 rounded-full ${CATEGORY_COLORS[e.category] || 'bg-gray-500'}`}>
      <Icon size={10} />{cat?.[`name_${language}`] || cat?.name_es || e.category}
    </span>
  );
};

const timeLabel = (e, t, lang, today) => {
  const parts = [e.event_time ? e.event_time.slice(0, 5) : t.allDay];
  if (e.end_date && e.end_date > e.event_date && e.end_date >= today) parts.push(`${t.until} ${fmtDay(e.end_date, lang, { weekday: 'short', day: 'numeric', month: 'short' })}`);
  return parts.join(' · ');
};

const BigCard = ({ e, t, language, today, categories, isPast, onClick }) => (
  <button onClick={onClick} className={`relative w-full aspect-[16/9] rounded-2xl overflow-hidden shadow-md text-left active:scale-[0.99] transition-transform ${isPast ? 'opacity-50' : ''}`} data-testid={`event-${e.id}`}>
    <img src={eventImage(e)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
    <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
      <div className="flex items-center gap-1">
        <CategoryChip e={e} categories={categories} language={language} />
        {e.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/90 text-[#9C4A2E]"><Star size={10} fill="currentColor" />{t.featured}</span>}
      </div>
      <Tags e={e} t={t} />
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-4">
      <h3 className="text-white text-lg font-bold leading-tight line-clamp-2 drop-shadow">{e[`title_${language}`] || e.title_es}</h3>
      <p className="text-white/85 text-xs mt-1 flex items-center gap-1 flex-wrap"><Clock size={12} />{timeLabel(e, t, language, today)}{e.venue_name && <><span>·</span><MapPin size={12} /><span className="truncate max-w-[55%]">{e.venue_name}</span></>}</p>
    </div>
  </button>
);

const RowCard = ({ e, t, language, today, categories, isPast, onClick }) => (
  <button onClick={onClick} className={`w-full flex bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 text-left active:scale-[0.99] transition-transform ${isPast ? 'opacity-50' : ''}`} data-testid={`event-${e.id}`}>
    <div className="relative w-[34%] flex-shrink-0 aspect-square">
      <img src={eventImage(e)} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
    </div>
    <div className="flex-1 min-w-0 p-3 flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <CategoryChip e={e} categories={categories} language={language} />
        {isFreeEvent(e) && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="event-tag-free">{t.free}</span>}
      </div>
      <h3 className="font-semibold text-[#1D1D1F] text-sm leading-snug line-clamp-2 mt-1.5">{e[`title_${language}`] || e.title_es}</h3>
      <p className="text-xs text-gray-500 mt-auto pt-1.5 flex items-center gap-1 flex-wrap"><Clock size={11} />{timeLabel(e, t, language, today)}{e.venue_name && <><span>·</span><span className="truncate">{e.venue_name}</span></>}</p>
      <div className="mt-1"><Tags e={{ ...e, is_free: false, price_info: '' }} t={t} /></div>
    </div>
  </button>
);

export const EventsList = ({ events, categories, language, onSelect }) => {
  const t = T[language] || T.es;
  const today = canaryToday();
  const tomorrow = addDays(today, 1);
  const nowHM = new Intl.DateTimeFormat('en-GB', { timeZone: 'Atlantic/Canary', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date());

  const byDay = {};
  for (const e of events) {
    const start = e.event_date || '';
    const end = e.end_date && e.end_date > start ? e.end_date : start;
    if (end < today) continue;
    const day = start < today ? today : start;
    (byDay[day] ||= []).push(e);
  }
  const days = Object.keys(byDay).sort();
  if (!days.length) return null;

  return (
    <div className="space-y-6" data-testid="events-by-day">
      {days.map(day => {
        const label = day === today ? `${t.today} · ${fmtDay(day, language)}` : day === tomorrow ? `${t.tomorrow} · ${fmtDay(day, language)}` : fmtDay(day, language);
        const items = byDay[day].sort((a, b) => (a.event_time || '99').localeCompare(b.event_time || '99'));
        return (
          <section key={day} data-testid={`events-day-${day}`}>
            <h2 className={`text-[11px] font-bold uppercase tracking-wider mb-3 ${day === today || day === tomorrow ? 'text-[#005A63]' : 'text-gray-500'}`} data-testid="events-day-label">{label}</h2>
            <div className="space-y-3">
              {items.map(e => {
                const isPast = day === today && e.event_date === today && e.event_time && e.event_time.slice(0, 5) < nowHM && !(e.end_date && e.end_date > today);
                const Card = day === today || e.featured ? BigCard : RowCard;
                return <Card key={e.id} e={e} t={t} language={language} today={today} categories={categories} isPast={isPast} onClick={() => onSelect(e)} />;
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default EventsList;
